import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { scaleTime } from "d3-scale";
import { discontinuousTimeScaleProviderBuilder } from "react-stockcharts/lib/scale";
import { ChartCanvas, Chart } from "react-stockcharts";
import { AreaSeries, BarSeries } from "react-stockcharts/lib/series";
import { XAxis, YAxis } from "react-stockcharts/lib/axes";
import { fitWidth } from "react-stockcharts/lib/helper";
import { last, first } from "react-stockcharts/lib/utils";
// import { Brush } from "react-stockcharts/lib/interactive";
import {
  getNewChartConfig,
  getChartConfigWithUpdatedYScales,
  getCurrentCharts,
  getCurrentItem
} from "react-stockcharts/lib/utils/ChartDataUtil";

import {
  InteractiveYCoordinate,
  ClickCallback
} from "react-stockcharts/lib/interactive";

import Slider from "rc-slider";
import PreviewChart from "./PreviewChart";

const Range = Slider.Range;

const RCSliderStyle = styled.div`
  .rc-slider {
    border: 1px solid #ccc;
    position: relative;
    height: 60px;
    padding: 0;
    width: 100%;    
    -ms-touch-action: none;
    touch-action: none;
    box-sizing: border-box;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  }
  .rc-slider * {
    box-sizing: border-box;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  }
  .rc-slider-rail {
    position: absolute;
    width: 100%;
   
    height: 100%;
  }
  .rc-slider-track {
    position: absolute;
    left: 0;
    height: 60px;
    border-radius: 6px;
    
  }
  .rc-slider-handle {
    position: absolute;
    margin-left: -7px;
    width: 14px;
    height: 100%;
    cursor: pointer;
    cursor: -webkit-grab;
    cursor: grab;
    background-color: rgba(115,115,115,.3);    
    -ms-touch-action: pan-x;
    touch-action: pan-x;
  }
  .rc-slider-handle:focus {
    border-color: #57c5f7;
    box-shadow: 0 0 0 5px #96dbfa;
    outline: none;
  }
  .rc-slider-handle-click-focused:focus {
    border-color: #96dbfa;
    box-shadow: unset;
  }
  .rc-slider-handle:hover {
    border-color: #57c5f7;
  }
  .rc-slider-handle:active {
    border-color: #57c5f7;
    box-shadow: 0 0 5px #57c5f7;
    cursor: -webkit-grabbing;
    cursor: grabbing;
  }
  .rc-slider-mark {
    position: absolute;
    top: 18px;
    left: 0;
    width: 100%;
    font-size: 12px;
  }
  .rc-slider-mark-text {
    position: absolute;
    display: inline-block;
    vertical-align: middle;
    text-align: center;
    cursor: pointer;
    color: #999;
  }
  .rc-slider-mark-text-active {
    color: #666;
  }
  .rc-slider-step {
    position: absolute;
    width: 100%;
    height: 4px;
    background: transparent;
  }
  .rc-slider-dot {
    position: absolute;
    bottom: -2px;
    margin-left: -4px;
    width: 8px;
    height: 8px;
    border: 2px solid #e9e9e9;
    background-color: #fff;
    cursor: pointer;
    border-radius: 50%;
    vertical-align: middle;
  }
  .rc-slider-dot-active {
    border-color: #96dbfa;
  }
  .rc-slider-disabled {
    background-color: #e9e9e9;
  }
  .rc-slider-disabled .rc-slider-track {
    background-color: #ccc;
  }
  .rc-slider-disabled .rc-slider-handle,
  .rc-slider-disabled .rc-slider-dot {
    border-color: #ccc;
    box-shadow: none;
    background-color: #fff;
    cursor: not-allowed;
  }
  .rc-slider-disabled .rc-slider-mark-text,
  .rc-slider-disabled .rc-slider-dot {
    cursor: not-allowed !important;
  }
  .rc-slider-vertical {
    width: 14px;
    height: 100%;
    padding: 0 5px;
  }
  .rc-slider-vertical .rc-slider-rail {
    height: 100%;
    width: 4px;
  }
  .rc-slider-vertical .rc-slider-track {
    left: 5px;
    bottom: 0;
    width: 4px;
  }
  .rc-slider-vertical .rc-slider-handle {
    margin-left: -5px;
    margin-bottom: -7px;
    -ms-touch-action: pan-y;
    touch-action: pan-y;
  }
  .rc-slider-vertical .rc-slider-mark {
    top: 0;
    left: 18px;
    height: 100%;
  }
  .rc-slider-vertical .rc-slider-step {
    height: 100%;
    width: 4px;
  }
  .rc-slider-vertical .rc-slider-dot {
    left: 2px;
    margin-bottom: -4px;
  }
  .rc-slider-vertical .rc-slider-dot:first-child {
    margin-bottom: -4px;
  }
  .rc-slider-vertical .rc-slider-dot:last-child {
    margin-bottom: -4px;
  }
  .rc-slider-tooltip-zoom-down-enter,
  .rc-slider-tooltip-zoom-down-appear {
    animation-duration: 0.3s;
    animation-fill-mode: both;
    display: block !important;
    animation-play-state: paused;
  }
  .rc-slider-tooltip-zoom-down-leave {
    animation-duration: 0.3s;
    animation-fill-mode: both;
    display: block !important;
    animation-play-state: paused;
  }
  .rc-slider-tooltip-zoom-down-enter.rc-slider-tooltip-zoom-down-enter-active,
  .rc-slider-tooltip-zoom-down-appear.rc-slider-tooltip-zoom-down-appear-active {
    animation-name: rcSliderTooltipZoomDownIn;
    animation-play-state: running;
  }
  .rc-slider-tooltip-zoom-down-leave.rc-slider-tooltip-zoom-down-leave-active {
    animation-name: rcSliderTooltipZoomDownOut;
    animation-play-state: running;
  }
  .rc-slider-tooltip-zoom-down-enter,
  .rc-slider-tooltip-zoom-down-appear {
    transform: scale(0, 0);
    animation-timing-function: cubic-bezier(0.23, 1, 0.32, 1);
  }
  .rc-slider-tooltip-zoom-down-leave {
    animation-timing-function: cubic-bezier(0.755, 0.05, 0.855, 0.06);
  }
  @keyframes rcSliderTooltipZoomDownIn {
    0% {
      opacity: 0;
      transform-origin: 50% 100%;
      transform: scale(0, 0);
    }
    100% {
      transform-origin: 50% 100%;
      transform: scale(1, 1);
    }
  }
  @keyframes rcSliderTooltipZoomDownOut {
    0% {
      transform-origin: 50% 100%;
      transform: scale(1, 1);
    }
    100% {
      opacity: 0;
      transform-origin: 50% 100%;
      transform: scale(0, 0);
    }
  }
  .rc-slider-tooltip {
    position: absolute;
    left: -9999px;
    top: -9999px;
    visibility: visible;
    box-sizing: border-box;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  }
  .rc-slider-tooltip * {
    box-sizing: border-box;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  }
  .rc-slider-tooltip-hidden {
    display: none;
  }
  .rc-slider-tooltip-placement-top {
    padding: 4px 0 8px 0;
  }
  .rc-slider-tooltip-inner {
    padding: 6px 2px;
    min-width: 24px;
    height: 24px;
    font-size: 12px;
    line-height: 1;
    color: #fff;
    text-align: center;
    text-decoration: none;
    background-color: #6c6c6c;
    border-radius: 6px;
    box-shadow: 0 0 4px #d9d9d9;
  }
  .rc-slider-tooltip-arrow {
    position: absolute;
    width: 0;
    height: 0;
    border-color: transparent;
    border-style: solid;
  }
  .rc-slider-tooltip-placement-top .rc-slider-tooltip-arrow {
    bottom: 4px;
    left: 50%;
    margin-left: -4px;
    border-width: 4px 4px 0;
    border-top-color: #6c6c6c;
  }

  .rc-slider-handle-1 {
    &:before {
      content: "";
      display: block;
      width: 100vw; 
      height: 100%;    
      position: absolute;
      top: 0;
      right: 100%;
      background-color rgba(0,0,0,.1);
      pointer-events: none;
    }
    /* &:after {
      content: "||";
      display: block;
      width: 18px; 
      height: 25px;    
      position: absolute;
      top: clac(50% - 12px);
      right: 50%;
      background-color rgba(0,0,0,.1);
      pointer-events: none;
    } */
    &:after {
      content: "";
      display: block;
      width: 1px;         
      position: absolute;
      top: 5px;
      bottom: 5px;
      right: 50%;
      background-color rgba(110,110,110, .6);
      pointer-events: none;
    } 
  }

  .rc-slider-handle-2 {
    &:before {
      content: "";
      display: block;
      width: 100vw; 
      height: 100%;    
      position: absolute;
      top: 0;
      left: 100%;
      background-color rgba(0,0,0,.1);
      pointer-events: none;
    }
    &:after {
      content: "";
      display: block;
      width: 1px;         
      position: absolute;
      top: 5px;
      bottom: 5px;
      left: 50%;
      background-color rgba(110,110,110, .6);
      pointer-events: none;
    } 
  }

`;

const Button = styled.button`
  height: 29px;

  color: #fff;
  font-size: 1rem;
  border: none;
  outline: none;
  background-color: rgba(19, 93, 128, 1);
  padding: 0.2em 0.5em;
  cursor: pointer;

  &:hover {
    background-color: rgba(19, 93, 128, 0.8);
    color: #fff;
  }
  &:first-child {
    border-top-left-radius: 3px;
    border-bottom-left-radius: 3px;
    padding-left: 0.8em;
  }
  &:last-child {
    border-top-right-radius: 3px;
    border-bottom-right-radius: 3px;
    padding-right: 0.8em;
  }

  ${props =>
    props.active &&
    `
  background-color: rgba(0, 0, 0, 1);
  color: #fff;
`};
`;

class AreaChart extends React.PureComponent {
  render() {
    return (
      <RCSliderStyle>
        <Range {...this.props} />
      </RCSliderStyle>
    );
  }
}

export default AreaChart;

//-----------------------------------------------
//----------------------------------------------- Range
//-----------------------------------------------

//-------------------------------
//-------------------------------
//-------------------------------
// const SliderContainer = styled.div`
//   display: inline-block;
//   width: 100%;
//   height: 60px;
//   position: relative;
// `;

// const Gague = styled.div`
//   position: relative
//   height: 60px;
//   background-color: transparent;
//   touch-action: none;
//   width: 120px;
//   &:after {
//       content: "";
//       display: block;
//       width: 100vw;
//       height: 100%;
//       position: absolute;
//       top: 0;
//       left: 100%;
//       background-color rgba(0,0,0,.2);
//   }
//   &:before {
//       content: "";
//       display: block;
//       width: 100vw;
//       height: 100%;
//       position: absolute;
//       top: 0;
//       right: 100%;
//       background-color rgba(0,0,0,.2);
//   }
// `;

// class Slider2 extends React.PureComponent {
//   constructor(props) {
//     super(props);
//     this.element = React.createRef();
//   }

//   componentDidMount() {
//     const node = this.element.current;
//     interact(node)
//       .draggable({
//         // onmove: e => {
//         //   this.props.onMove(e);
//         // },
//         inertia: false,
//         restrict: {
//           restriction: "parent",
//           elementRect: { top: 0, left: 0, bottom: 1, right: 1 }
//         }
//       })
//       .resizable({
//         edges: { left: true, right: true, bottom: false, top: false },
//         restrictEdges: {
//           outer: "parent"
//         },
//         // minimum size
//         restrictSize: {
//           min: { width: 10, height: 60 }
//         },
//         inertia: false
//       })
//       .on("resizemove", event => {
//         this.props.onMove2(event);
//         // var target = event.target,
//         //   x = parseFloat(target.getAttribute("data-x")) || 0,
//         //   y = parseFloat(target.getAttribute("data-y")) || 0;
//         // // update the element's style
//         // target.style.width = event.rect.width + "px";
//         // target.style.height = event.rect.height + "px";
//         // // translate when resizing from top or left edges
//         // x += event.deltaRect.left;
//         // y += event.deltaRect.top;
//         // target.style.webkitTransform = target.style.transform =
//         //   "translate(" + x + "px," + y + "px)";
//         // target.setAttribute("data-x", x);
//         // target.setAttribute("data-y", y);
//         // // target.textContent =
//         // //   Math.round(event.rect.width) +
//         // //   "\u00D7" +
//         // //   Math.round(event.rect.height);
//       })
//       .on("dragend resizeend", e => {
//         //console.log("dragend", e);
//         this.props.onChange && this.props.onChange(e);
//       })
//       .on("dragmove", e => {
//         this.props.onMove(e);
//       });

//     // function dragMoveListener(event) {
//     //   var target = event.target,
//     //     // keep the dragged position in the data-x/data-y attributes
//     //     x = (parseFloat(target.getAttribute("data-x")) || 0) + event.dx,
//     //     y = (parseFloat(target.getAttribute("data-y")) || 0) + event.dy;

//     //   // translate the element
//     //   target.style.webkitTransform = target.style.transform =
//     //     "translate(" + x + "px, " + y + "px)";

//     //   // update the posiion attributes
//     //   target.setAttribute("data-x", x);
//     //   target.setAttribute("data-y", y);
//     // }
//   }

//   render() {
//     const style = {
//       width: `${this.props.width}px`,
//       height: "60px",
//       transform: `translate(${this.props.x}px, 0px)`
//     };
//     return (
//       <SliderContainer>
//         <Gague innerRef={this.element} style={style} />
//       </SliderContainer>
//     );
//   }
// }
